import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Loader2, AlertTriangle, UserCircle, Flame, Sun, CalendarDays, IdCard, Phone, Mail, MapPin,
} from "lucide-react";

const EMPTY = {
  type: "Privato", first_name: "", last_name: "", company_name: "", contact_person: "",
  fiscal_code: "", vat_number: "", phone_primary: "", email: "", comune: "",
  address: "", civico: "", cap: "", provincia: "",
  office_id: "", assigned_operator_id: "",
};

export default function NewCustomerDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [step, setStep] = useState("form");
  const [form, setForm] = useState(EMPTY);
  const [offices, setOffices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [duplicate, setDuplicate] = useState(null);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep("form"); setForm(EMPTY); setError(""); setDuplicate(null); setCreated(null);
    api.get("/offices").then(({ data }) => setOffices(data)).catch(() => {});
    if (isAdmin) api.get("/users").then(({ data }) => setOperators(data.filter((u) => u.active))).catch(() => {});
  }, [open, isAdmin]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isPrivato = form.type === "Privato";

  const save = async () => {
    setError(""); setDuplicate(null); setSaving(true);
    try {
      const payload = { ...form, office_id: form.office_id || null, assigned_operator_id: form.assigned_operator_id || null };
      const { data } = await api.post("/customers", payload);
      setCreated(data);
      setStep("choose");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.existing) setDuplicate(detail.existing);
      else setError(formatApiErrorDetail(detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const goTo = (path) => { onOpenChange(false); navigate(path); };
  const openExisting = () => goTo(`/clienti/${duplicate.id}`);
  const afterChoice = (label) => {
    if (label) toast.info(`${label}: modulo disponibile nella fase successiva.`);
    goTo(`/clienti/${created.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-lg" data-testid="new-customer-dialog">
        {step === "form" && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Nuovo Cliente — inserimento rapido</DialogTitle>
              <DialogDescription>Pochi campi essenziali. I dettagli si completano nella scheda cliente.</DialogDescription>
            </DialogHeader>

            {duplicate && (
              <div className="rounded-lg border border-warning/30 bg-warning/10 p-3" data-testid="duplicate-warning">
                <div className="flex items-center gap-2 font-semibold text-warning"><AlertTriangle className="h-4 w-4" /> POSSIBILE CLIENTE GIÀ PRESENTE</div>
                <div className="mt-2 space-y-0.5 text-sm text-slate-700">
                  <div className="font-semibold">{duplicate.display_name} <span className="font-normal text-slate-500">· {duplicate.type}</span></div>
                  {duplicate.phone_primary && <div className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {duplicate.phone_primary}</div>}
                  {duplicate.email && <div className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> {duplicate.email}</div>}
                  {duplicate.comune && <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {duplicate.comune}{duplicate.office_name ? ` · ${duplicate.office_name}` : ""}</div>}
                </div>
                <Button size="sm" className="mt-2" onClick={openExisting} data-testid="open-existing-customer">APRI CLIENTE ESISTENTE</Button>
              </div>
            )}
            {error && <div className="rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="new-customer-error">{error}</div>}

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Tipologia</Label>
                <Select value={form.type} onValueChange={(v) => set("type", v)}>
                  <SelectTrigger data-testid="nc-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="Privato">Privato</SelectItem>
                    <SelectItem value="Azienda">Azienda</SelectItem>
                    <SelectItem value="Condominio">Condominio</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {isPrivato ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Nome *</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="nc-first-name" /></div>
                  <div className="space-y-1.5"><Label>Cognome *</Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="nc-last-name" /></div>
                </div>
              ) : (
                <>
                  <div className="space-y-1.5"><Label>{form.type === "Azienda" ? "Ragione sociale *" : "Denominazione condominio *"}</Label><Input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="nc-company-name" /></div>
                  <div className="space-y-1.5"><Label>{form.type === "Azienda" ? "Referente" : "Amministratore / Referente"}</Label><Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} data-testid="nc-contact-person" /></div>
                </>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Telefono principale</Label><Input value={form.phone_primary} onChange={(e) => set("phone_primary", e.target.value)} data-testid="nc-phone" /></div>
                <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="nc-email" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Codice fiscale</Label><Input value={form.fiscal_code} onChange={(e) => set("fiscal_code", e.target.value)} data-testid="nc-fiscal-code" /></div>
                {!isPrivato && <div className="space-y-1.5"><Label>Partita IVA</Label><Input value={form.vat_number} onChange={(e) => set("vat_number", e.target.value)} data-testid="nc-vat" /></div>}
              </div>
              <div className="grid grid-cols-8 gap-3">
                <div className="col-span-5 space-y-1.5"><Label>Indirizzo (via/piazza)</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="nc-address" /></div>
                <div className="col-span-3 space-y-1.5"><Label>Civico</Label><Input value={form.civico} onChange={(e) => set("civico", e.target.value)} data-testid="nc-civico" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>CAP</Label><Input value={form.cap} onChange={(e) => set("cap", e.target.value)} data-testid="nc-cap" /></div>
                <div className="space-y-1.5"><Label>Provincia</Label><Input value={form.provincia} maxLength={4} onChange={(e) => set("provincia", e.target.value)} data-testid="nc-provincia" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Comune</Label><Input value={form.comune} onChange={(e) => set("comune", e.target.value)} data-testid="nc-comune" /></div>
                <div className="space-y-1.5">
                  <Label>Sede</Label>
                  <Select value={form.office_id || "none"} onValueChange={(v) => set("office_id", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="nc-office-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="none">Nessuna</SelectItem>
                      {offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Operatore assegnato</Label>
                {isAdmin ? (
                  <Select value={form.assigned_operator_id || "self"} onValueChange={(v) => set("assigned_operator_id", v === "self" ? "" : v)}>
                    <SelectTrigger data-testid="nc-operator-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="self">Io ({user?.full_name})</SelectItem>
                      {operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex items-center gap-2 rounded-md border border-input bg-slate-50 px-3 py-2 text-sm text-slate-600"><UserCircle className="h-4 w-4" /> {user?.full_name} (tu)</div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="nc-cancel">Annulla</Button>
              <Button onClick={save} disabled={saving} data-testid="nc-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva e continua"}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "choose" && created && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Cosa dobbiamo fare per questo cliente?</DialogTitle>
              <DialogDescription>{created.display_name} è stato creato. Scegli come procedere.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Luce / Gas", icon: Flame, mod: "Luce/Gas" },
                { label: "Efficienza Energetica", icon: Sun, mod: "Efficienza Energetica" },
                { label: "Appuntamento", icon: CalendarDays, mod: "Appuntamento" },
                { label: "Solo Anagrafica", icon: IdCard, mod: null },
              ].map((c) => (
                <button
                  key={c.label}
                  onClick={() => afterChoice(c.mod)}
                  data-testid={`choose-${c.mod ? c.mod.replace(/[^a-z]/gi, "-").toLowerCase() : "solo-anagrafica"}`}
                  className="flex flex-col items-center gap-2 rounded-xl border border-border bg-white p-5 text-center transition-all hover:border-brand hover:bg-brand/5 hover:shadow-sm"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand"><c.icon className="h-6 w-6" /></span>
                  <span className="text-sm font-semibold text-slate-800">{c.label}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
